import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import InfoBox from '../InfoBox';
import Disclaimer from '../Disclaimer';

import { useRules } from '../../lib/rules';
import { colourValidator } from '../../lib/validators';
import { parseTags, normaliseKey } from '../../lib/string';

const ColourScreen = ({ state, step, onNext }) => {
  const [info, setInfo] = useState(step.info || null);

  const { heading, subHeading = null, autoSubmit = true, field } = step;
  const { name = heading, component: FieldComponent, rules, options, ...props } = field;
  const normalisedName = normaliseKey(name);

  const { setValue, getValues } = useFormContext();

  const [screenState, setScreenState] = useState({
    hasLoaded: false,
    filteredOptions: options,
    brassPigment: null,
    colourError: null,
  });

  const quizType = localStorage.getItem('quiz-type');

  useEffect(() => {
    if (quizType === 'supplement') {

      // Set whether the user has selected a "Green Tea Extract" optional ingredient.
      const { yourFormulation: formulation = "" } = state;
      const hasGreenTeaExtract = formulation.includes('Green Tea Extract');

      // Hide colours that are not compatible with "Green Tea Extract".
      const filteredOptions = options.map((option) => {
        option.disabled = hasGreenTeaExtract && (
          option.value === 'Blue'
          || option.value === 'Light blue'
          || option.value === 'Pink'
          || option.value === 'Purple'
        );
        return option;
      });

      setScreenState({ ...screenState, hasLoaded: true, filteredOptions });
    }

    if (quizType === 'haircare') {
      // Set whether the user has selected a "Reduce brassiness..." hair goal.
      const {chooseUpToThreeHairGoals: goals = []} = state;
      let brassPigment = null;

      for (let i = 0; i < goals.length; i++) {
        if (goals[i] === 'Reduce brassiness for blondes') {
          brassPigment = 'blonde';
          break;
        }

        if (goals[i] === 'Reduce brassiness for brunettes') {
          brassPigment = 'brunette';
          break;
        }
      }

      // Set brassiness pigment if the user has selected a "Reduce brassiness..." hair goal.
      const filteredOptions = options.filter((option) => {
        const {brassiness = null, value} = option;

        if (brassPigment !== null) {
          if (brassiness !== null) {
            if (brassiness === brassPigment) {
              setValue(normalisedName, value);
            } else {
              return false;
            }
          }
        } else if (brassiness !== null) {
          return false;
        }

        return true;
      }).map((option) => {
        const {brassiness = null} = option;
        option.disabled = brassPigment !== null && brassiness === null;
        return option;
      });

      setScreenState({ ...screenState, hasLoaded: true, brassPigment, filteredOptions });
    }
  }, [state, screenState.brassPigment]);

  // Prevents unwanted setValues.
  if (!screenState.hasLoaded) {
    return null;
  }

  const { title: disclaimerTitle = null, body: disclaimerBody = null } = step.disclaimer || {};

  return (
    <div className={`quiz-section color-options colour-screen`}>
      <div className="quiz-section-inner text-center">
        <div className="form-label-wrapper">
          <h3 className="form-label">{parseTags(heading, state)}</h3>
          {!!subHeading && <p>{subHeading}</p>}
        </div>
        <FieldComponent
          defaultValue={state[normalisedName] || ''}
          rules={Array.isArray(rules) && useRules(rules, state)}
          name={name}
          options={screenState.filteredOptions}
          autoSubmit={autoSubmit}
          onNext={onNext}
          customError={screenState.colourError}
          onCloseError={() => setScreenState({ ...screenState, colourError: null })}
          onOptionsLoaded={() => {
            if (quizType === 'supplement') {
              setValue(normalisedName, state[normalisedName] || 'No colouring');
            }

            if (quizType === 'haircare') {
              const {brassPigment} = screenState;

              if (brassPigment === null) {
                return;
              }

              options.forEach((option) => {
                const {brassiness = null, value} = option;
                // Select the brassiness option when the user has selected a "Reduce brassiness" hair goal.
                if (brassPigment !== null && brassiness === brassPigment) {
                  setValue(normalisedName, value);
                  return false;
                }
              });
            }
          }}
          onOptionChange={(option) => {
            const { info: description = null, disabled = false, value } = option;

            let colourError = null;

            if (disabled) {
              colourError = colourValidator(value, state);
            } else {
              if (typeof description === 'string' && description) {
                setInfo({ title: null, description });
              } else {
                setInfo(description || step.info || null);
              }
            }

            setScreenState({ ...screenState, info, colourError });
          }}
          {...props}
        />
      </div>
      <div className={`quiz-section-bottom ${disclaimerTitle !== null ? 'has-learn-more' : ''}`}>
        {info !== null && <InfoBox title={info.title} info={info.description} />}
        {disclaimerTitle !== null && <Disclaimer title={disclaimerTitle} body={disclaimerBody} />}
        {info !== null && <div className="info-box-spacer" />}
      </div>
    </div>
  );
};

ColourScreen.propTypes = {
  state: PropTypes.shape().isRequired,
  step: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    subHeading: PropTypes.string,
    disclaimer: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.oneOfType([PropTypes.elementType, PropTypes.shape()]),
    }),
    info: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    autoSubmit: PropTypes.bool,
    field: PropTypes.shape({
      name: PropTypes.string,
      component: PropTypes.elementType.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          brassiness: PropTypes.string,
        }),
      ).isRequired,
      rules: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape()])),
    }),
  }).isRequired,
  onNext: PropTypes.func.isRequired,
};

export default ColourScreen;
